import React from 'react';
import { Link } from 'react-router-dom';
import {
  CpuChipIcon,
  ShieldCheckIcon,
  ServerIcon,
  SparklesIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  DocumentCheckIcon,
  CloudIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { SEOHead, seoData } from '../hooks/useSEO';

const Services: React.FC = () => {
  const services = [
    {
      id: 1,
      icon: CpuChipIcon,
      title: 'AI Development Services',
      description: 'Advanced artificial intelligence solutions including real-time voice systems, machine learning models, and intelligent automation platforms designed for enterprise deployment.',
      features: [
        'Real-Time AI Voice Systems',
        'OpenAI GPT-4o Integration',
        'WebSocket-Based Voice Routing',
        'Containerized AI Microservices',
        'Voice Activity Detection',
        'Dynamic Function Calling Systems',
        'Machine Learning Models',
        'Natural Language Processing',
        'Computer Vision Systems'
      ],
      benefits: [
        'Deploy production-ready AI systems',
        'Integrate cutting-edge voice technology',
        'Scale with containerized architecture',
        'Automate complex decision processes'
      ],
      relatedCapabilities: ['ai-voice-systems', 'custom-security-tools', 'infrastructure-automation']
    },
    {
      id: 2,
      icon: ShieldCheckIcon,
      title: 'Enterprise Cybersecurity',
      description: 'Comprehensive cybersecurity solutions with proven enterprise-grade methodologies, compliance frameworks, and advanced threat protection for mission-critical environments.',
      features: [
        'Enterprise Vulnerability Management',
        'Advanced Penetration Testing',
        'Incident Response & Forensics',
        'NIST SP 800-53/800-171 Compliance',
        'DFARS Implementation',
        'ISO 27001 Control Implementation',
        'Risk Management Framework (RMF)',
        'POA&M Management',
        'Cloud Security Architecture',
        'Endpoint Hardening (Windows/Linux/MacOS)',
        'Custom Security Tool Development'
      ],
      benefits: [
        'Meet government & enterprise compliance',
        'Proven Fortune 500 methodologies',
        'Advanced threat detection & response',
        'Comprehensive risk management'
      ],
      relatedCapabilities: ['enterprise-security', 'compliance-management', 'custom-security-tools']
    },
    {
      id: 3,
      icon: SparklesIcon,
      title: 'Infrastructure Automation',
      description: 'Advanced automation solutions using enterprise-grade tools and methodologies to streamline operations, enforce policies, and optimize infrastructure management.',
      features: [
        'SaltStack Infrastructure Automation',
        'Security Policy Enforcement',
        'Vulnerability Report Integration',
        'CI/CD Pipeline Development',
        'Network Protocol Analysis',
        'Automated Compliance Monitoring',
        'Custom Python/Perl Scripting',
        'System Configuration Management',
        'Performance Monitoring & Alerting'
      ],
      benefits: [
        'Automate policy enforcement',
        'Reduce manual configuration errors',
        'Accelerate deployment cycles',
        'Ensure consistent compliance'
      ],
      relatedCapabilities: ['infrastructure-automation', 'enterprise-security', 'legacy-modernization']
    },
    {
      id: 4,
      icon: ServerIcon,
      title: 'Enterprise Infrastructure',
      description: 'Complete infrastructure solutions including large-scale network design, security appliance integration, and enterprise hardware deployment with proven methodologies.',
      features: [
        'Large-Scale Fiber Network Design',
        'Security Appliance Integration',
        'VMware Infrastructure Deployment',
        'Load Balancer Configuration',
        'Firewall & IDS/IPS Systems',
        'Enterprise Server & Workstation Supply',
        'Network Security Architecture',
        'High-Availability System Design',
        'Infrastructure Monitoring Solutions'
      ],
      benefits: [
        'Government-grade network design',
        'Enterprise-scale infrastructure',
        'High-availability architectures',
        'Comprehensive security integration'
      ],
      relatedCapabilities: ['network-design', 'network-compliance', 'infrastructure-automation']
    },
    {
      id: 5,
      icon: DocumentCheckIcon,
      title: 'Compliance & Risk Management',
      description: 'Comprehensive compliance framework implementation and risk management services with proven experience in government and enterprise environments.',
      features: [
        'NIST Framework Implementation',
        'DFARS Compliance Consulting',
        'ISO 27001 Management Systems',
        'Risk Assessment Services',
        'Audit Preparation & Support',
        'Security Documentation',
        'Policy Development & Implementation',
        'Compliance Validation & Testing',
        'Remediation Planning & Execution'
      ],
      benefits: [
        'Achieve regulatory compliance',
        'Reduce audit findings',
        'Streamline compliance processes',
        'Minimize compliance risks'
      ],
      relatedCapabilities: ['compliance-management', 'enterprise-security', 'enterprise-training']
    },
    {
      id: 6,
      icon: CloudIcon,
      title: 'Cloud Security & Integration',
      description: 'Advanced cloud security architecture, risk assessments, and integration services with expertise in multi-platform environments and threat modeling.',
      features: [
        'Cloud Security Architecture',
        'Multi-Platform Integration',
        'Threat Modeling & Risk Assessment',
        'Cloud Migration Security',
        'Hybrid Infrastructure Design',
        'Container Security (Kubernetes)',
        'DevSecOps Implementation',
        'Cloud Compliance Management',
        'Security Monitoring & Alerting'
      ],
      benefits: [
        'Secure cloud transformations',
        'Reduce cloud security risks',
        'Optimize hybrid architectures',
        'Ensure cloud compliance'
      ],
      relatedCapabilities: ['cloud-security', 'legacy-modernization', 'infrastructure-automation']
    },
    {
      id: 7,
      icon: WrenchScrewdriverIcon,
      title: 'Advanced Security & Infrastructure Services',
      description: 'Enterprise-grade foundational services enhanced with AI, automation, and compliance integration. From intelligent backup solutions to real-time file integrity monitoring.',
      features: [
        'AI-Enhanced Backup & Recovery Solutions',
        'Advanced Surveillance & Camera Systems',
        'Next-Generation Endpoint Protection',
        'Real-Time File Integrity Monitoring',
        'Enterprise Change Management Systems',
        'Advanced Identity Management Solutions',
        'Automated Compliance Reporting',
        'Intelligent Threat Detection',
        'Centralized Security Management'
      ],
      benefits: [
        'AI-powered predictive capabilities',
        'Real-time monitoring and alerting',
        'Automated compliance integration',
        'Enterprise-scale management'
      ],
      relatedCapabilities: ['custom-security-tools', 'compliance-management', 'enterprise-security']
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Security Assessment & Discovery',
      description: 'Comprehensive analysis of current systems, security posture, compliance gaps, and infrastructure requirements using proven enterprise methodologies.'
    },
    {
      step: '02',
      title: 'Architecture & Compliance Planning',
      description: 'Design secure, compliant solutions aligned with NIST, DFARS, and ISO 27001 frameworks, incorporating risk management and threat modeling.'
    },
    {
      step: '03',
      title: 'Secure Implementation',
      description: 'Deploy solutions using enterprise-grade security practices, automated testing, and continuous integration with minimal operational disruption.'
    },
    {
      step: '04',
      title: 'Validation & Penetration Testing',
      description: 'Rigorous security testing, vulnerability assessments, and compliance validation to ensure robust, production-ready systems.'
    },
    {
      step: '05',
      title: 'Training & Continuous Support',
      description: 'Technical team training, documentation, ongoing security monitoring, and continuous improvement support.'
    }
  ];


  return (
    <div className="min-h-screen">
      <SEOHead {...seoData.services} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-red-600 text-white section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6 animate-fade-in">
              Professional IT & Cybersecurity Services in Cheyenne, Wyoming
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed animate-slide-up animation-delay-200">
              Enterprise-grade IT support, cybersecurity, and technology solutions serving Wyoming businesses with proven
              methodologies from Fortune 500 and government environments. Transform your Cheyenne area operations
              with cutting-edge technology and comprehensive compliance frameworks.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Comprehensive IT Services for Wyoming Businesses
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We deliver enterprise-grade technology solutions to Cheyenne and Wyoming businesses with proven methodologies from
              Fortune 500 and government environments, specializing in IT support, advanced
              cybersecurity, and comprehensive compliance frameworks for local companies.
            </p>
          </div>
          
          <div className="space-y-12">
            {services.map((service, index) => (
              <div key={service.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={`animate-slide-up ${index % 2 === 1 ? 'lg:col-start-2' : ''}`} style={{ animationDelay: `${index * 200}ms` }}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                      <service.icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-heading font-bold text-slate-900">
                      {service.title}
                    </h3>
                  </div>
                  
                  <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Key Features:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start space-x-2 text-sm text-slate-700">
                            <CheckCircleIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Benefits:</h4>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, benefitIndex) => (
                          <li key={benefitIndex} className="flex items-start space-x-2 text-sm text-slate-700">
                            <ArrowRightIcon className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className={`animate-fade-in ${index % 2 === 1 ? 'lg:col-start-1' : ''}`} style={{ animationDelay: `${index * 200 + 100}ms` }}>
                  <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                    <div className="aspect-video bg-gradient-to-br from-blue-50 to-red-50 rounded-xl flex items-center justify-center mb-6">
                      <service.icon className="w-24 h-24 text-blue-400" />
                    </div>
                    <div className="text-center">
                      <h4 className="text-lg font-semibold text-slate-900 mb-2">
                        Ready to get started?
                      </h4>
                      <p className="text-slate-600 mb-4">
                        Contact us for a free consultation and see how we can transform your business.
                      </p>
                      <div className="space-y-3">
                        <Link to="/book" className="btn-primary w-full text-center block">
                          Get Free Consultation
                        </Link>
                        <div className="text-center">
                          <Link
                            to="/capabilities"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center space-x-1"
                          >
                            <span>View Technical Capabilities</span>
                            <ArrowRightIcon className="w-3 h-3" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Capabilities Section */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Wyoming's Premier IT & Cybersecurity Technical Capabilities
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Dive deeper into our technical expertise serving Cheyenne and Wyoming businesses with detailed capability showcases,
              real-world implementations, and proven case studies from our local projects.
            </p>
          </div>
          
          <div className="text-center">
            <Link
              to="/capabilities"
              className="btn-primary inline-flex items-center space-x-2 text-lg px-8 py-4"
            >
              <span>View All 12 Capabilities</span>
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Wyoming Service Area Section */}
      <section className="bg-blue-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              IT Services Tailored for Wyoming Industries
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              We understand the unique technology challenges facing Wyoming businesses and provide specialized IT solutions for key industries throughout the state.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-blue-100">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Energy Sector IT</h3>
              <p className="text-slate-600 mb-4">
                Specialized cybersecurity and compliance solutions for Wyoming's oil, gas, and renewable energy companies, including NERC CIP compliance and industrial control system security.
              </p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• NERC CIP Compliance</li>
                <li>• SCADA System Security</li>
                <li>• Critical Infrastructure Protection</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm border border-blue-100">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Government Contractors</h3>
              <p className="text-slate-600 mb-4">
                DFARS and NIST 800-171 compliance solutions for Wyoming businesses working with federal contracts, ensuring secure handling of controlled unclassified information.
              </p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• DFARS Compliance</li>
                <li>• NIST 800-171 Implementation</li>
                <li>• CUI Protection</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm border border-blue-100">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Small & Medium Business</h3>
              <p className="text-slate-600 mb-4">
                Comprehensive IT support and cybersecurity solutions designed for Wyoming's small and medium businesses, providing enterprise-grade security at affordable rates.
              </p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• 24/7 IT Support</li>
                <li>• Managed Cybersecurity</li>
                <li>• Cloud Migration Services</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-8 shadow-sm border border-blue-100 max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-semibold text-slate-900 mb-2">Serving All of Wyoming</h3>
              <p className="text-slate-600">
                From our headquarters in Cheyenne, we provide comprehensive IT services throughout Wyoming
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-3">
                <div className="font-semibold text-slate-900">Cheyenne</div>
                <div className="text-sm text-slate-600">On-site & Remote</div>
              </div>
              <div className="p-3">
                <div className="font-semibold text-slate-900">Casper</div>
                <div className="text-sm text-slate-600">Remote Support</div>
              </div>
              <div className="p-3">
                <div className="font-semibold text-slate-900">Laramie</div>
                <div className="text-sm text-slate-600">Remote Support</div>
              </div>
              <div className="p-3">
                <div className="font-semibold text-slate-900">Statewide</div>
                <div className="text-sm text-slate-600">24/7 Remote</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Our Proven Process
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our proven methodology combines enterprise security practices with comprehensive
              compliance frameworks to deliver secure, scalable, and compliant technology solutions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center animate-slide-up" style={{ animationDelay: `${index * 150}ms` }}>
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {step.description}
                </p>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-blue-200 transform translate-x-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-slate-900 to-blue-900 text-white section-padding">
        <div className="container-max text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Ready to Transform Your Wyoming Business with Professional IT Services?
          </h2>
          <p className="text-xl text-slate-200 mb-8 max-w-2xl mx-auto">
            Let our Cheyenne-based IT experts help you leverage cutting-edge technology to drive growth,
            enhance security, and optimize your Wyoming business operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="btn-primary bg-white text-blue-600 hover:bg-blue-50">
              Schedule Free Consultation
            </Link>
            <Link to="/contact" className="btn-outline border-white text-white hover:bg-white hover:text-blue-600">
              Contact Us Today
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;